import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { supabaseAuth } from '../api/supabaseAuth'
import { useAuthStore } from '../stores/authStore'

/**
 * Where a provider sign-in lands.
 *
 * The Supabase client consumes the session from the URL fragment on load (detectSessionInUrl), so
 * by the time this runs there is either a session or the sign-in failed. Everything after that is
 * the same as a password sign-in: resolve the token to a platform user, put both in the store.
 *
 * A provider can authenticate someone we have no artist record for — a Google account whose email
 * matches nothing here. That is not an error to hide: they signed in successfully and still cannot
 * enter, so it needs to say why rather than bouncing them back to a login screen that looks like
 * it simply did not work.
 */
export function AuthCallback() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  // Strict mode double-invokes effects; without this the exchange runs twice.
  const ran = useRef(false)

  useEffect(() => {
    if (ran.current) return
    ran.current = true

    ;(async () => {
      try {
        const accessToken = await supabaseAuth.currentAccessToken()
        if (!accessToken) {
          setError('Sign-in did not complete. Please try again.')
          return
        }

        const user = await supabaseAuth.loadPlatformUser(accessToken)
        useAuthStore.getState().setToken(accessToken)
        await useAuthStore.getState().setUserSync(user)
        navigate('/app', { replace: true })
      } catch (e) {
        // Authenticated by the provider, but no artist here matches. Say so plainly — the account
        // needs linking, and "try again" would be wrong advice.
        console.error('[auth] provider callback failed', e)
        setError(
          'You signed in, but this account is not linked to an artist profile yet. ' +
          'Please contact support, or sign in with your email and password.',
        )
        await supabaseAuth.signOut().catch(() => {})
      }
    })()
  }, [navigate])

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      {error ? (
        <div className="max-w-sm text-center">
          <p className="text-sm text-destructive">{error}</p>
          <button
            className="mt-4 text-sm underline"
            onClick={() => navigate('/', { replace: true })}
          >
            Back to sign in
          </button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Finishing sign-in…</p>
      )}
    </div>
  )
}
